import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2, ArrowRight, Check, GripVertical, User as UserIcon, Rows3, LayoutGrid } from "lucide-react";
import { useConfirm } from "../../components/ConfirmDialog";
import { useCompany } from "../../context/CompanyContext";
import { useAuth } from "../../context/AuthContext";
import { CompanySwitcher } from "./AdminLayout";

const STAGES = [
  { key: "nuevo", label: "Nuevo", color: "#9BA7B3" },
  { key: "contactado", label: "Contactado", color: "#7BA6B0" },
  { key: "calificado", label: "Calificado", color: "#5B8FAF" },
  { key: "propuesta", label: "Propuesta", color: "#D97757" },
  { key: "negociacion", label: "Negociación", color: "#D9A557" },
  { key: "ganado", label: "Ganado", color: "#1B7E3F" },
  { key: "perdido", label: "Perdido", color: "#9B1C1C" },
];
const STAGE_LABEL = Object.fromEntries(STAGES.map((s) => [s.key, s.label]));

const fmtMoney = (v, c) =>
  c === "CLP"
    ? `$${Math.round(v || 0).toLocaleString("es-CL")}`
    : `US$${(v || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const EMPTY = {
  name: "", email: "", phone: "", company: "", position: "",
  source: "manual", stage: "nuevo", value: "", value_monthly: "", currency: "CLP",
  notes: "", next_action: "", next_action_date: "",
};

export default function AdminCRM() {
  const [prospects, setProspects] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draggedId, setDraggedId] = useState(null);
  const [hoverStage, setHoverStage] = useState(null);
  const [compact, setCompact] = useState(() => localStorage.getItem("dosoft.crm_compact") === "1");
  const justDraggedRef = useRef(false);
  const confirm = useConfirm();
  const { activeId, active, companies, isAll } = useCompany();
  const { isAdmin } = useAuth();

  // Build a lookup of company_id → { name, color } for chip rendering on cards.
  const companyById = companies.reduce((acc, c) => { acc[c.id] = c; return acc; }, {});

  // Load sellers once (admin only) for the assign dropdown in the form.
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin/users").then((r) => setSellers(r.data.filter((u) => u.role === "seller" && u.is_active))).catch(() => {});
  }, [isAdmin]);

  const load = () => {
    const params = !isAll && activeId ? { company_id: activeId } : {};
    return api.get("/admin/prospects", { params }).then((r) => setProspects(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeId, isAll]);

  const byStage = (s) => prospects.filter((p) => p.stage === s);
  const totalValue = (s) =>
    byStage(s).reduce((acc, p) => acc + (Number(p.value) || 0), 0);
  const totalValueMonthly = (s) =>
    byStage(s).reduce((acc, p) => acc + (Number(p.value_monthly) || 0), 0);

  const moveStage = async (id, newStage) => {
    const prev = prospects.find((p) => p.id === id);
    if (!prev || prev.stage === newStage) return;
    // optimistic update
    setProspects((arr) => arr.map((p) => (p.id === id ? { ...p, stage: newStage } : p)));
    try {
      await api.patch(`/admin/prospects/${id}`, { stage: newStage });
      toast.success("Etapa actualizada");
    } catch (e) {
      // rollback
      setProspects((arr) => arr.map((p) => (p.id === id ? { ...p, stage: prev.stage } : p)));
      toast.error("No se pudo mover el prospecto");
    }
  };

  const onDragStart = (e, id) => {
    setDraggedId(id);
    justDraggedRef.current = true;
    try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", id); } catch (_) {}
  };
  const onDragEnd = () => {
    setDraggedId(null);
    setHoverStage(null);
    justDraggedRef.current = false;
  };
  const onColumnDragOver = (e) => { e.preventDefault(); try { e.dataTransfer.dropEffect = "move"; } catch (_) {} };
  const onColumnDrop = (e, stageKey) => {
    e.preventDefault();
    const id = draggedId || e.dataTransfer.getData("text/plain");
    if (id) moveStage(id, stageKey);
    setDraggedId(null);
    setHoverStage(null);
    // The source card may unmount due to the optimistic state update, so
    // onDragEnd might never fire. Clear the flag here as a safety net.
    setTimeout(() => { justDraggedRef.current = false; }, 50);
  };

  return (
    <div data-testid="admin-crm-page" className="p-6 md:p-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand font-mono mb-1">
            CRM{isAll ? " · Todas las empresas" : (active ? ` · ${active.name}` : "")}
          </div>
          <h1 className="font-display font-bold text-2xl md:text-3xl text-ash-100">Funnel de Ventas</h1>
          <p className="text-ash-300 text-sm mt-2">
            {isAll
              ? "Vista consolidada · prospectos de todas las empresas."
              : active
              ? "Seguimiento de prospectos a lo largo del pipeline comercial."
              : "Selecciona una empresa para ver y gestionar prospectos."}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            data-testid="crm-toggle-compact"
            onClick={() => {
              const next = !compact;
              setCompact(next);
              localStorage.setItem("dosoft.crm_compact", next ? "1" : "0");
            }}
            className="h-11 inline-flex items-center gap-2 border border-ink-500 hover:border-brand hover:text-brand text-ash-300 px-4 uppercase tracking-widest text-[10px] font-mono"
            title={compact ? "Cambiar a vista detallada" : "Cambiar a vista compacta"}
          >
            {compact ? <LayoutGrid size={14} strokeWidth={1.6} /> : <Rows3 size={14} strokeWidth={1.6} />}
            {compact ? "Detallada" : "Compacta"}
          </button>
          <CompanySwitcher />
        <button
          data-testid="crm-new-prospect"
          onClick={() => setEditing({ ...EMPTY, company_id: !isAll && activeId ? activeId : "" })}
          className="h-11 inline-flex items-center gap-2 bg-brand text-ink-800 font-semibold hover:bg-brand-hover px-5 uppercase tracking-wider text-xs"
        >
          <Plus size={14} /> Nuevo prospecto
        </button>
        </div>
      </div>

      {/* Kanban */}
      <div className="pb-4">
        <div className="grid grid-cols-7 gap-2">
          {STAGES.map((stage) => {
            const list = byStage(stage.key);
            return (
              <div
                key={stage.key}
                data-testid={`crm-column-${stage.key}`}
                onDragOver={onColumnDragOver}
                onDragEnter={() => setHoverStage(stage.key)}
                onDragLeave={(e) => {
                  // only clear if leaving the column entirely
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setHoverStage((s) => (s === stage.key ? null : s));
                  }
                }}
                onDrop={(e) => onColumnDrop(e, stage.key)}
                className={`min-w-0 bg-ink-800 border flex flex-col max-h-[70vh] transition-colors ${
                  hoverStage === stage.key ? "border-brand" : "border-ink-500"
                }`}
              >
                <div className="p-3 border-b border-ink-500 flex items-center justify-between" style={{ borderTopColor: stage.color, borderTopWidth: 3, borderTopStyle: "solid" }}>
                  <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-ash-100">{stage.label}</div>
                    <div className="text-[10px] font-mono text-ash-500 mt-0.5">
                      {list.length} · {fmtMoney(totalValue(stage.key), "CLP")}
                      {totalValueMonthly(stage.key) > 0 && (
                        <span className="text-[#D97757]"> + {fmtMoney(totalValueMonthly(stage.key), "CLP")}/m</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="p-2 space-y-2 overflow-y-auto flex-1">
                  {list.map((p) => (
                    <div
                      key={p.id}
                      data-testid={`crm-card-${p.id}`}
                      draggable
                      onDragStart={(e) => onDragStart(e, p.id)}
                      onDragEnd={onDragEnd}
                      onClick={() => {
                        if (justDraggedRef.current) return;
                        setEditing({
                          ...p,
                          value: p.value || "",
                          next_action_date: p.next_action_date ? p.next_action_date.slice(0, 10) : "",
                        });
                      }}
                      className={`group bg-ink-700 border cursor-grab active:cursor-grabbing transition-all select-none ${
                        compact ? "p-2" : "p-3"
                      } ${
                        draggedId === p.id ? "opacity-40 border-brand" : "border-ink-500 hover:border-brand"
                      }`}
                    >
                      {compact ? (
                        (() => {
                          const co = p.company_id ? companyById[p.company_id] : null;
                          const col = co?.color || "#7BA6B0";
                          return (
                            <div className="flex items-center gap-2 min-w-0">
                              <span
                                className="w-1.5 h-7 shrink-0"
                                style={{ background: col, boxShadow: `0 0 6px ${col}66` }}
                                title={co?.name || ""}
                              />
                              <div className="min-w-0 flex-1">
                                <div className="text-[13px] text-ash-100 font-medium truncate leading-tight">{p.name}</div>
                                <div className="flex items-center gap-2 mt-0.5 text-[10px] font-mono text-ash-500 truncate">
                                  {p.company && <span className="truncate max-w-[140px]">{p.company}</span>}
                                  {Number(p.value) > 0 && (
                                    <span className="text-brand shrink-0">{fmtMoney(p.value, p.currency)}</span>
                                  )}
                                  {Number(p.value_monthly) > 0 && (
                                    <span className="text-[#D97757] shrink-0">+{fmtMoney(p.value_monthly, p.currency)}/m</span>
                                  )}
                                </div>
                              </div>
                              {isAdmin && p.owner_name && (
                                <UserIcon size={11} strokeWidth={2} className="text-brand shrink-0" title={p.owner_name} />
                              )}
                            </div>
                          );
                        })()
                      ) : (
                      <div className="flex items-start gap-2">
                        <GripVertical size={12} className="text-ash-500 mt-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                        <div className="min-w-0 flex-1">
                          {(() => {
                            const co = p.company_id ? companyById[p.company_id] : null;
                            if (!co) return null;
                            const col = co.color || "#7BA6B0";
                            return (
                              <div
                                className="inline-flex items-center gap-1.5 mb-1.5 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold text-white shadow-sm"
                                style={{ background: col, boxShadow: `0 0 0 1px ${col}, 0 0 8px ${col}66` }}
                                title={`Empresa: ${co.name}`}
                              >
                                <span className="w-1.5 h-1.5 bg-white/70" />
                                <span className="truncate max-w-[120px]">{co.name}</span>
                              </div>
                            );
                          })()}
                          <div className="text-sm text-ash-100 font-medium truncate">{p.name}</div>
                          {p.company && <div className="text-[11px] text-ash-300 mt-0.5 truncate">{p.company}</div>}
                          {(Number(p.value) > 0 || Number(p.value_monthly) > 0) && (
                            <div className="text-xs font-mono mt-2">
                              {Number(p.value) > 0 && (
                                <span className="text-brand">{fmtMoney(p.value, p.currency)}</span>
                              )}
                              {Number(p.value_monthly) > 0 && (
                                <span className="text-[#D97757] block">+ {fmtMoney(p.value_monthly, p.currency)}/mes</span>
                              )}
                            </div>
                          )}
                          {p.next_action && (
                            <div className="text-[10px] text-ash-500 mt-2 flex items-center gap-1">
                              <ArrowRight size={10} /> <span className="truncate">{p.next_action}</span>
                            </div>
                          )}
                          {isAdmin && p.owner_name && (
                            <div className="mt-2 inline-flex items-center gap-1 bg-ink-800 border border-ink-500/70 px-1.5 py-0.5 text-[10px] text-ash-300 font-mono">
                              <UserIcon size={9} strokeWidth={1.8} className="text-brand" />
                              <span className="truncate max-w-[120px]">{p.owner_name}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      )}
                    </div>
                  ))}
                  {list.length === 0 && (
                    <div className={`text-center text-xs py-6 border border-dashed ${
                      hoverStage === stage.key ? "border-brand text-brand" : "border-transparent text-ash-500"
                    }`}>
                      {hoverStage === stage.key ? "Soltar aquí" : "Sin prospectos"}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {editing && (
        <ProspectModal
          prospect={editing}
          stages={STAGES}
          companies={companies}
          sellers={sellers}
          isAdmin={isAdmin}
          onSave={async (data) => {
            const payload = {
              ...data,
              value: Number(data.value) || 0,
              value_monthly: Number(data.value_monthly) || 0,
              next_action_date: data.next_action_date
                ? new Date(data.next_action_date + "T00:00:00-04:00").toISOString()
                : null,
            };
            // New prospects: respect what the user picked in the form;
            // only fall back to the active company if the form left it empty
            // and we're not in "Todas las empresas" mode.
            if (!editing.id && !data.company_id && !isAll && activeId) {
              payload.company_id = activeId;
            }
            try {
              if (editing.id) {
                await api.patch(`/admin/prospects/${editing.id}`, payload);
                toast.success("Prospecto actualizado");
              } else {
                await api.post("/admin/prospects", payload);
                toast.success("Prospecto creado");
              }
              setEditing(null);
              load();
            } catch (e) {
              toast.error(formatApiError(e.response?.data?.detail) || "Error");
            }
          }}
          onDelete={async () => {
            const ok = await confirm({
              title: "¿Eliminar prospecto?",
              message: "Esta acción es permanente. ¿Continuar?",
              confirmLabel: "Eliminar",
              variant: "danger",
            });
            if (!ok) return;
            await api.delete(`/admin/prospects/${editing.id}`);
            toast.success("Prospecto eliminado");
            setEditing(null);
            load();
          }}
          onClose={() => setEditing(null)}
          onMove={(stage) => moveStage(editing.id, stage)}
        />
      )}
    </div>
  );
}

function ProspectModal({ prospect, stages, companies, sellers = [], isAdmin = false, onSave, onDelete, onClose, onMove }) {
  const [form, setForm] = useState(prospect);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isNew = !prospect.id;

  return (
    <div className="fixed inset-0 z-50 bg-ink-900/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-800 border border-ink-500 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-ink-500 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-brand font-mono mb-1">
              {isNew ? "Nuevo prospecto" : "Editar prospecto"}
            </div>
            <h2 className="font-display font-bold text-xl text-ash-100">
              {form.name || "Sin nombre"}
            </h2>
          </div>
          <button onClick={onClose} className="text-ash-300 hover:text-ash-100"><X size={20} /></button>
        </div>

        {!isNew && (
          <div className="px-6 pt-5">
            <div className="text-[10px] uppercase tracking-widest text-ash-300 mb-2 font-mono">Etapa actual</div>
            <div className="grid grid-cols-7 gap-1.5">
              {stages.map((s) => (
                <button
                  key={s.key}
                  onClick={() => { update("stage", s.key); onMove(s.key); }}
                  className={`px-2 py-1.5 border text-[10px] uppercase tracking-widest transition-colors text-center ${
                    form.stage === s.key ? "text-white border-transparent" : "border-ink-500 text-ash-100 hover:border-brand"
                  }`}
                  style={form.stage === s.key ? { backgroundColor: s.color, borderColor: s.color } : {}}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="Empresa (CRM)">
              <select
                data-testid="prospect-company-select"
                value={form.company_id || ""}
                onChange={(e) => update("company_id", e.target.value || null)}
                className={inputCls}
              >
                <option value="">— Sin asignar —</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}{!c.is_active ? " (inactiva)" : ""}</option>
                ))}
              </select>
            </F>
            {isAdmin && (
              <F label="Vendedor">
                <select
                  data-testid="prospect-owner-select"
                  value={form.owner_id || ""}
                  onChange={(e) => update("owner_id", e.target.value || null)}
                  className={inputCls}
                >
                  <option value="">— Sin asignar —</option>
                  {sellers.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                  {form.owner_id && !sellers.find((s) => s.id === form.owner_id) && form.owner_name && (
                    <option value={form.owner_id}>{form.owner_name} (inactivo)</option>
                  )}
                </select>
              </F>
            )}
            <F label="Empresa cliente"><I v={form.company} on={(v) => update("company", v)} /></F>
            <F label="Nombre *"><I v={form.name} on={(v) => update("name", v)} /></F>
            <F label="Cargo"><I v={form.position} on={(v) => update("position", v)} /></F>
            <F label="Email"><I type="email" v={form.email} on={(v) => update("email", v)} /></F>
            <F label="Teléfono"><I v={form.phone} on={(v) => update("phone", v)} /></F>
            <F label="Fuente"><I v={form.source} on={(v) => update("source", v)} /></F>
            <F label="Valor único">
              <CurrencyInput value={form.value} currency={form.currency} on={(v) => update("value", v)} />
            </F>
            <F label="Valor mensual">
              <CurrencyInput value={form.value_monthly} currency={form.currency} on={(v) => update("value_monthly", v)} />
            </F>
            <F label="Moneda">
              <select value={form.currency} onChange={(e) => update("currency", e.target.value)} className={inputCls}>
                <option>CLP</option><option>USD</option><option>EUR</option>
              </select>
            </F>
            <F label="Próxima acción"><I v={form.next_action} on={(v) => update("next_action", v)} /></F>
            <F label="Fecha próxima acción">
              <I type="date" v={form.next_action_date || ""} on={(v) => update("next_action_date", v)} />
            </F>
          </div>
          <F label="Notas">
            <textarea rows={4} value={form.notes || ""} onChange={(e) => update("notes", e.target.value)} className={inputCls} placeholder="Historial, observaciones..." />
          </F>
        </div>

        <div className="px-6 py-4 border-t border-ink-500 flex items-center justify-between gap-3">
          {!isNew ? (
            <button onClick={onDelete} className="text-xs uppercase tracking-widest text-destructive hover:opacity-80 inline-flex items-center gap-2">
              <Trash2 size={14} /> Eliminar
            </button>
          ) : <span />}
          <button onClick={() => onSave(form)} className="bg-brand text-ink-800 font-semibold hover:bg-brand-hover px-5 py-3 uppercase tracking-wider text-xs inline-flex items-center gap-2">
            <Check size={14} strokeWidth={2.5} /> {isNew ? "Crear prospecto" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-ink-900 border border-ink-500 text-ash-100 focus:border-brand focus:ring-1 focus:ring-brand outline-none px-3 py-2 text-sm placeholder:text-ash-500";

function F({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-semibold text-ash-300 uppercase tracking-widest mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

function I({ v, on, type = "text" }) {
  return <input type={type} value={v || ""} onChange={(e) => on(e.target.value)} className={inputCls} />;
}

function CurrencyInput({ value, currency, on }) {
  const isCLP = currency === "CLP";
  const symbol = isCLP ? "$" : currency === "USD" ? "US$" : currency === "EUR" ? "€" : "$";
  const format = (n) => {
    if (n === "" || n === null || n === undefined) return "";
    const num = Number(n);
    if (isNaN(num)) return "";
    if (isCLP) return `${symbol}${Math.round(num).toLocaleString("es-CL")}`;
    return `${symbol}${num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };
  const handleChange = (e) => {
    const raw = e.target.value;
    if (isCLP) {
      const digits = raw.replace(/\D/g, "");
      on(digits === "" ? "" : Number(digits));
    } else {
      // strip thousand separators and currency, keep one dot for decimals
      const cleaned = raw.replace(/[^\d.]/g, "");
      const firstDot = cleaned.indexOf(".");
      const normalized = firstDot === -1
        ? cleaned
        : cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, "");
      if (normalized === "" || normalized === ".") { on(""); return; }
      // keep trailing dot while user is typing
      if (normalized.endsWith(".")) { on(normalized); return; }
      const num = parseFloat(normalized);
      on(isNaN(num) ? "" : num);
    }
  };
  return (
    <input
      type="text"
      inputMode="decimal"
      value={format(value)}
      onChange={handleChange}
      placeholder={symbol + "0"}
      className={inputCls}
    />
  );
}
